import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import apiClient from '@/utils/api';
import { useAuth } from '@/App';
import { CallBookingDialog } from './student-profile/CallBookingDialog';
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { WorkflowDialogContent } from './ui/workflow-dialog';
import { CompactDateField } from './ui/compact-date-field';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Phone, PhoneOff, ClipboardEdit, GraduationCap, Calendar, Clock,
  CheckCircle2, AlertTriangle, XCircle, Users, RefreshCw, Ban,
} from 'lucide-react';

const COLORS = { primary: '#192650', accent: '#0DA891', border: '#E2E8F0' };

const NC_REASONS = ['No Answer', 'Busy', 'Switched Off', 'Not Reachable', 'Invalid/Wrong Number', 'Call Rejected', 'Requested Call Back'];
const SPOKE_WITH = ['Student', 'Parent', 'Student & Parent', 'Guardian / Relative'];
const NQ_REASONS = ['Academic Eligibility Not Met', 'Budget Issue', 'Not Interested', 'Intake Mismatch', 'Parent Not Supportive', 'Course/Country Not Available', 'Other'];
const SPAM_REASONS = ['Fake Lead', 'Duplicate Lead', 'Wrong Number', 'Irrelevant Inquiry', 'Test Lead', 'Other'];
const INTAKES = [
  { v: 'september_2025', l: 'September 2025' }, { v: 'february_2026', l: 'February 2026' },
  { v: 'september_2026', l: 'September 2026' }, { v: 'february_2027', l: 'February 2027' },
];

const EMPTY = {
  response: '', not_connected_reason: '', nc_next_action: '',
  spoke_with: '', qualification: '', next_action: '', disqual_reason: '', other_reason: '',
  follow_up_date: '', eligible_intake: '', notes: '',
};

// Static class strings so Tailwind JIT keeps them
const CHIP_SELECTED = {
  indigo: 'bg-indigo-50 border-indigo-400 text-indigo-700',
  rose: 'bg-rose-50 border-rose-400 text-rose-700',
  amber: 'bg-amber-50 border-amber-400 text-amber-700',
  teal: 'bg-teal-50 border-teal-400 text-teal-700',
};

// Small selectable chip
const Chip = ({ label, selected, onClick, color = 'indigo', testid }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    data-testid={testid}
    className={`call-chip px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
      selected ? CHIP_SELECTED[color] : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
    }`}
  >
    {label}
  </button>
);

const SectionLabel = ({ children }) => (
  <Label className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: COLORS.primary }}>{children}</Label>
);

const DateTimeField = ({ testid, ...props }) => <CompactDateField {...props} testId={testid} label="Date · IST" withTime min={new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' })} />;

const QualBtn = ({ id, label, sub, icon: Icon, sel, txt, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    data-testid={`qualification-${id}`}
    className={`call-qualification flex-1 p-2.5 rounded-lg border-2 text-center transition-colors ${selected ? sel : 'border-gray-200 hover:border-gray-300'}`}
  >
    <Icon className={`w-4 h-4 mx-auto mb-1 ${selected ? txt : 'text-gray-400'}`} />
    <div className={`text-xs font-bold ${selected ? txt : 'text-gray-600'}`}>{label}</div>
    <div className="text-[9px] text-gray-400">{sub}</div>
  </button>
);

const NextActionRow = ({ id, label, tag, icon: Icon, needsDate, optionalDate, selected, onSelect, followUpDate, onFollowUpChange, loadMap, heavyThreshold, aggregate }) => (
  <div>
    <button type="button" aria-pressed={selected}
      onClick={onSelect}
      data-testid={`next-action-${id}`}
      className={`call-next-action w-full p-2 rounded-lg border-2 text-left transition-colors flex items-center gap-2 ${selected ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-indigo-200'}`}
    >
      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${selected ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300'}`}>
        {selected && <CheckCircle2 className="w-2.5 h-2.5 text-white" />}
      </div>
      <Icon className="w-3.5 h-3.5 text-indigo-600" />
      <span className="text-xs font-medium text-gray-700">{label}</span>
      {tag && <span className="hidden sm:inline ml-auto text-[9px] px-1.5 py-0.5 rounded-full text-indigo-600 bg-indigo-100">{tag}</span>}
    </button>
    {selected && (needsDate || optionalDate) && (
      <DateTimeField value={followUpDate} onChange={onFollowUpChange} testid={`${id}-date`} optional={optionalDate} loadMap={loadMap} heavyThreshold={heavyThreshold} aggregate={aggregate} />
    )}
  </div>
);

const ReasonChips = ({ reasons, value, otherValue, onSelect, onOtherChange }) => (
  <div>
    <SectionLabel>Reason <span className="text-red-500">*</span></SectionLabel>
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {reasons.map((r) => (
        <Chip key={r} label={r} selected={value === r} color="rose"
          onClick={() => onSelect(r)}
          testid={`reason-${r.toLowerCase().replace(/[^a-z]/g, '')}`} />
      ))}
    </div>
    {value === 'Other' && (
      <Input value={otherValue} onChange={(e) => onOtherChange(e.target.value)}
        placeholder="Specify reason…" className="mt-2 h-8 text-xs" data-testid="reason-other-input" />
    )}
  </div>
);

/**
 * Guided "Update Call Response" dialog.
 * Props: open, onOpenChange, lead {id, full_name, student_id, rnr_consecutive}, onSaved(responseData)
 */
export default function CallResponseDialog({ open, onOpenChange, lead, onSaved, sourceTaskId }) {
  const { user } = useAuth();
  const telecaller = user?.role === 'telecaller';
  const [booking, setBooking] = useState(null);
  const [f, setF] = useState({ ...EMPTY });
  const [submitting, setSubmitting] = useState(false);
  const [requestId, setRequestId] = useState(null);
  const [loadMap, setLoadMap] = useState({});
  const [heavyThreshold, setHeavyThreshold] = useState(50);
  const [loadAggregate, setLoadAggregate] = useState(false);
  const set = (patch) => setF((prev) => ({ ...prev, ...patch }));

  useEffect(() => { if (open) { setF({ ...EMPTY }); setRequestId(crypto.randomUUID()); } }, [open, lead?.id, user?.role]);

  // Fetch the upcoming per-day task load so the calendar can show how busy each
  // day already is (helps the counsellor plan when scheduling a follow-up).
  useEffect(() => {
    if (!open || !lead?.id) return undefined;
    setLoadMap({}); setLoadAggregate(false);
    // The counsellor's legacy load endpoint does not admit telecallers.
    if (telecaller) return undefined;
    let cancelled = false;
    const run = async () => {
      const today = new Date();
      const end = new Date(today); end.setDate(today.getDate() + 75);
      const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const params = { start: fmt(today), end: fmt(end) };
      if (lead.assigned_counselor_id) params.counselor_id = lead.assigned_counselor_id;
      try {
        const r = await apiClient.get('/my-tasks/upcoming-load', { params });
        if (!cancelled) {
          setLoadMap(r.data?.loads || {});
          setHeavyThreshold(r.data?.heavy_threshold || 50);
          setLoadAggregate(r.data?.scope === 'all');
        }
      } catch { /* non-fatal — badges just won't show */ }
    };
    run();
    return () => { cancelled = true; };
  }, [open, lead?.id, lead?.assigned_counselor_id, telecaller]);

  const resolvedReason = f.disqual_reason === 'Other' ? (f.other_reason.trim() || 'Other') : f.disqual_reason;

  const disabled = () => {
    if (!f.response) return true;
    if (f.response === 'not_connected') {
      if (!f.not_connected_reason) return true;
      if (!f.nc_next_action) return true;
      if (f.nc_next_action === 'retry' && !f.follow_up_date) return true;
      return false;
    }
    // connected
    if (!f.spoke_with) return true;
    if (!f.qualification) return true;
    if (!f.notes || !f.notes.trim()) return true;  // notes mandatory for connected calls
    if (f.qualification === 'qualified') {
      if (!f.next_action) return true;
      if (telecaller && !['book_counselling', 'follow_up', 'future_prospect'].includes(f.next_action)) return true;
      if (['follow_up', 'future_prospect'].includes(f.next_action) && !f.follow_up_date) return true;
    }
    if (f.qualification === 'not_qualified') {
      if (!f.disqual_reason || (f.disqual_reason === 'Other' && !f.other_reason.trim())) return true;
      if (!f.next_action) return true;
      if (f.next_action === 'future_follow_up' && !f.follow_up_date) return true;
    }
    if (f.qualification === 'spam') {
      if (!f.disqual_reason || (f.disqual_reason === 'Other' && !f.other_reason.trim())) return true;
    }
    if (f.qualification === 'pending') {
      if (!f.follow_up_date) return true;  // callback date mandatory
    }
    return false;
  };

  const handleSubmit = async () => {
    if (submitting || disabled()) return;
    setSubmitting(true);
    // Spam qualification → next action is always Close Lead
    const nextAction = f.response === 'not_connected'
      ? f.nc_next_action
      : (f.qualification === 'spam' ? 'close_lead'
         : f.qualification === 'pending' ? 'follow_up'
         : f.next_action);
    try {
      const r = await apiClient.post(`/students/${lead.id}/call-response`, {
        request_id: requestId,
        source_task_id: sourceTaskId || null,
        response: f.response,
        notes: f.notes || null,
        next_action: nextAction || null,
        spoke_with: f.response === 'connected' ? (f.spoke_with || null) : null,
        qualification_outcome: f.response === 'connected' ? ((f.qualification && f.qualification !== 'pending') ? f.qualification : null) : null,
        disqual_reason: ['not_qualified', 'spam'].includes(f.qualification) ? (resolvedReason || null) : null,
        not_connected_reason: f.response === 'not_connected' ? (f.not_connected_reason || null) : null,
        follow_up_date: f.follow_up_date || null,
        eligible_intake: f.eligible_intake || null,
      });

      const msg = {
        retry: 'Not Connected recorded — retry scheduled',
        mark_spam: 'Lead marked Spam/Junk → BAD LEADS',
        follow_up: 'Qualified — Follow-up scheduled ✅',
        future_prospect: 'Qualified — moved to FUTURE',
        convert_to_admission: 'Lead converted to Admission! 🎓',
        book_counselling: 'Call saved — choose a counselling slot',
        close_lead: f.qualification === 'spam' ? 'Lead marked Spam/Junk → BAD LEADS' : 'Lead closed → BAD LEADS',
      }[nextAction] || 'Call response saved';
      toast.success(telecaller && nextAction === 'follow_up' ? 'Call back scheduled' :
        telecaller && nextAction === 'future_prospect' ? 'Future follow-up scheduled' : msg);

      onOpenChange(false);
      if (nextAction === 'book_counselling') setBooking({ leadId: lead.id, notes: f.notes });
      window.dispatchEvent(new Event('tasks-changed'));
      onSaved && onSaved(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === 'object' && d.missing_fields) toast.error(`${d.message}\n\nMissing: ${d.missing_fields.join(', ')}`, { duration: 8000 });
      else if (d && typeof d === 'object' && d.message) toast.error(d.message, { duration: 8000 });
      else toast.error(typeof d === 'string' ? d : 'Failed to save call response');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <><Dialog open={open && !booking} onOpenChange={value => { if (!submitting) onOpenChange(value); }}>
      <WorkflowDialogContent
        className="call-response-compact"
        data-testid="call-response-dialog"
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onFocusOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="workflow-dialog-header">
          <DialogTitle className="flex items-center gap-2 text-sm" style={{ color: COLORS.primary }}>
            <ClipboardEdit className="w-4 h-4" style={{ color: COLORS.accent }} />
            Update Call Response
          </DialogTitle>
          <DialogDescription className="text-xs break-words" data-testid="call-response-lead">
            {lead && (
              <span>
                <strong style={{ color: COLORS.primary }}>{lead.full_name}</strong>
                {lead.student_id && <span style={{ color: COLORS.accent }}> ({lead.student_id})</span>}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="workflow-dialog-body space-y-3" data-testid="call-response-body">
          {/* STEP 1 */}
          <div>
            <SectionLabel>Step 1: Call Status *</SectionLabel>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button type="button" data-testid="call-response-connected"
                aria-pressed={f.response === 'connected'}
                onClick={() => set({ ...EMPTY, response: 'connected' })}
                className={`flex items-center justify-center gap-2 p-2.5 rounded-lg border-2 transition-all ${f.response === 'connected' ? 'border-emerald-500 bg-emerald-50 shadow-sm' : 'border-gray-200 hover:border-emerald-300'}`}>
                <Phone className={`w-4 h-4 ${f.response === 'connected' ? 'text-emerald-600' : 'text-gray-400'}`} />
                <span className={`text-xs font-semibold ${f.response === 'connected' ? 'text-emerald-700' : 'text-gray-700'}`}>Connected</span>
              </button>
              <button type="button" data-testid="call-response-not-connected"
                aria-pressed={f.response === 'not_connected'}
                onClick={() => set({ ...EMPTY, response: 'not_connected' })}
                className={`flex items-center justify-center gap-2 p-2.5 rounded-lg border-2 transition-all ${f.response === 'not_connected' ? 'border-red-500 bg-red-50 shadow-sm' : 'border-gray-200 hover:border-red-300'}`}>
                <PhoneOff className={`w-4 h-4 ${f.response === 'not_connected' ? 'text-red-600' : 'text-gray-400'}`} />
                <span className={`text-xs font-semibold ${f.response === 'not_connected' ? 'text-red-700' : 'text-gray-700'}`}>Not Connected</span>
              </button>
            </div>
          </div>

          {/* NOT CONNECTED */}
          {f.response === 'not_connected' && (
            <>
              <div>
                <SectionLabel>Reason <span className="text-red-500">*</span></SectionLabel>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {NC_REASONS.map((r) => (
                    <Chip key={r} label={r} selected={f.not_connected_reason === r} color="amber"
                      onClick={() => set({ not_connected_reason: r })}
                      testid={`nc-reason-${r.toLowerCase().replace(/[^a-z]/g, '')}`} />
                  ))}
                </div>
              </div>
              <div>
                <SectionLabel>Next Action *</SectionLabel>
                <div className="mt-1.5 grid grid-cols-2 gap-2">
                  <button type="button" data-testid="nc-action-retry"
                    aria-pressed={f.nc_next_action === 'retry'}
                    onClick={() => set({ nc_next_action: 'retry' })}
                    className={`flex items-center justify-center gap-1.5 p-2 rounded-lg border-2 text-xs font-medium transition-all ${f.nc_next_action === 'retry' ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-600'}`}>
                    <RefreshCw className="w-3.5 h-3.5" /> Retry
                  </button>
                  <button type="button" data-testid="nc-action-spam"
                    aria-pressed={f.nc_next_action === 'mark_spam'}
                    onClick={() => set({ nc_next_action: 'mark_spam', follow_up_date: '' })}
                    className={`flex items-center justify-center gap-1.5 p-2 rounded-lg border-2 text-xs font-medium transition-all ${f.nc_next_action === 'mark_spam' ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-200 text-gray-600'}`}>
                    <Ban className="w-3.5 h-3.5" /> Mark Spam/Junk
                  </button>
                </div>
              </div>
              {f.nc_next_action === 'retry' && (
                <div>
                  <SectionLabel>Retry Date &amp; Time <span className="text-red-500">*</span></SectionLabel>
                  <DateTimeField value={f.follow_up_date} onChange={(v) => set({ follow_up_date: v })} testid="retry-date" loadMap={loadMap} heavyThreshold={heavyThreshold} aggregate={loadAggregate} />
                </div>
              )}
            </>
          )}

          {/* CONNECTED */}
          {f.response === 'connected' && (
            <>
              {/* Step 2: Spoke With */}
              <div>
                <SectionLabel><Users className="w-3 h-3 inline mr-1" />Step 2: Spoke With *</SectionLabel>
                <div className="call-spoke-grid mt-1.5 grid grid-cols-2 gap-1.5">
                  {SPOKE_WITH.map((s) => (
                    <Chip key={s} label={s} selected={f.spoke_with === s} color="teal"
                      onClick={() => set({ spoke_with: s })}
                      testid={`spoke-with-${s.toLowerCase().replace(/[^a-z]/g, '')}`} />
                  ))}
                </div>
              </div>

              {/* Step 3: Qualification */}
              <div>
                <SectionLabel>Step 3: Lead Qualification *</SectionLabel>
                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <QualBtn id="qualified" label="Qualified" sub="Eligible & interested" icon={CheckCircle2}
                    sel="border-emerald-500 bg-emerald-50" txt="text-emerald-700"
                    selected={f.qualification === 'qualified'}
                    onClick={() => set({ qualification: 'qualified', next_action: '', disqual_reason: '', other_reason: '', follow_up_date: '' })} />
                  <QualBtn id="not_qualified" label="Not Qualified" sub="Genuine, not eligible" icon={AlertTriangle}
                    sel="border-amber-500 bg-amber-50" txt="text-amber-700"
                    selected={f.qualification === 'not_qualified'}
                    onClick={() => set({ qualification: 'not_qualified', next_action: '', disqual_reason: '', other_reason: '', follow_up_date: '' })} />
                  <QualBtn id="spam" label="Spam/Junk" sub="Fake / irrelevant" icon={XCircle}
                    sel="border-red-500 bg-red-50" txt="text-red-700"
                    selected={f.qualification === 'spam'}
                    onClick={() => set({ qualification: 'spam', next_action: '', disqual_reason: '', other_reason: '', follow_up_date: '' })} />
                  <QualBtn id="pending" label="Pending" sub="Qualify on callback" icon={Clock}
                    sel="border-sky-500 bg-sky-50" txt="text-sky-700"
                    selected={f.qualification === 'pending'}
                    onClick={() => set({ qualification: 'pending', next_action: '', disqual_reason: '', other_reason: '', follow_up_date: '' })} />
                </div>
              </div>

              {/* Qualification Pending → Callback Required */}
              {f.qualification === 'pending' && (
                <div data-testid="qual-pending-callback">
                  <SectionLabel>Callback Required *</SectionLabel>
                  <p className="text-xs text-gray-500 mt-0.5">Couldn't fully qualify on this call — schedule a callback to complete qualification.</p>
                  <div className="mt-1.5">
                    <DateTimeField value={f.follow_up_date} onChange={(v) => set({ follow_up_date: v })} testid="pending-callback-date" loadMap={loadMap} heavyThreshold={heavyThreshold} aggregate={loadAggregate} />
                  </div>
                </div>
              )}

              {/* Qualified → Next Action */}
              {f.qualification === 'qualified' && (
                <div>
                  <SectionLabel>Next Action *</SectionLabel>
                  <div className="mt-1.5 space-y-1.5">
                    {telecaller && <NextActionRow id="book_counselling" label="Book counselling" tag="→ Counsellor" icon={Calendar}
                      selected={f.next_action === 'book_counselling'}
                      onSelect={() => set({ next_action: 'book_counselling', follow_up_date: '', eligible_intake: '' })} />}
                    <NextActionRow id="follow_up" label={telecaller ? 'Call back' : 'Follow Up'} tag="→ Follow-ups" icon={Calendar} needsDate
                      selected={f.next_action === 'follow_up'}
                      onSelect={() => set({ next_action: 'follow_up', follow_up_date: f.follow_up_date })}
                      followUpDate={f.follow_up_date} onFollowUpChange={(v) => set({ follow_up_date: v })}
                      loadMap={loadMap} heavyThreshold={heavyThreshold} aggregate={loadAggregate} />
                    <NextActionRow id="future_prospect" label={telecaller ? 'Future follow-up' : 'Future Prospect'} tag="→ Future" icon={Clock} needsDate
                      selected={f.next_action === 'future_prospect'}
                      onSelect={() => set({ next_action: 'future_prospect', follow_up_date: f.follow_up_date })}
                      followUpDate={f.follow_up_date} onFollowUpChange={(v) => set({ follow_up_date: v })}
                      loadMap={loadMap} heavyThreshold={heavyThreshold} aggregate={loadAggregate} />
                    {!telecaller && <NextActionRow id="convert_to_admission" label="Convert to Admission" tag="→ Admissions" icon={GraduationCap}
                      selected={f.next_action === 'convert_to_admission'}
                      onSelect={() => set({ next_action: 'convert_to_admission', follow_up_date: '' })} />}
                  </div>
                  {f.next_action === 'future_prospect' && (
                    <div className="mt-2">
                      <SectionLabel>Eligible Intake <span className="text-gray-400">(optional)</span></SectionLabel>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {INTAKES.map((it) => (
                          <Chip key={it.v} label={it.l} selected={f.eligible_intake === it.v} color="amber"
                            onClick={() => set({ eligible_intake: f.eligible_intake === it.v ? '' : it.v })}
                            testid={`intake-${it.v}`} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Not Qualified → Reason + Next Action */}
              {f.qualification === 'not_qualified' && (
                <>
                  <ReasonChips reasons={NQ_REASONS} value={f.disqual_reason} otherValue={f.other_reason}
                    onSelect={(r) => set({ disqual_reason: r, other_reason: '' })}
                    onOtherChange={(v) => set({ other_reason: v })} />
                  <div>
                    <SectionLabel>Next Action *</SectionLabel>
                    <div className="mt-1.5 space-y-1.5">
                      <NextActionRow id="close_lead" label="Close Lead" tag="→ Bad Leads" icon={XCircle}
                        selected={f.next_action === 'close_lead'}
                        onSelect={() => set({ next_action: 'close_lead', follow_up_date: '' })} />
                      <NextActionRow id="future_follow_up" label="Future Follow Up" tag="→ Future" icon={Calendar} needsDate
                        selected={f.next_action === 'future_follow_up'}
                        onSelect={() => set({ next_action: 'future_follow_up', follow_up_date: f.follow_up_date })}
                        followUpDate={f.follow_up_date} onFollowUpChange={(v) => set({ follow_up_date: v })}
                        loadMap={loadMap} heavyThreshold={heavyThreshold} aggregate={loadAggregate} />
                    </div>
                  </div>
                </>
              )}

              {/* Spam → Reason (next action fixed: Close Lead) */}
              {f.qualification === 'spam' && (
                <>
                  <ReasonChips reasons={SPAM_REASONS} value={f.disqual_reason} otherValue={f.other_reason}
                    onSelect={(r) => set({ disqual_reason: r, other_reason: '' })}
                    onOtherChange={(v) => set({ other_reason: v })} />
                  <div data-testid="call-spam-close-warning" className="text-[11px] text-red-600 bg-red-50 border border-red-200 rounded-md px-2 py-1.5">
                    Next action: <strong>Close Lead</strong> → moves to Bad Leads (tagged Spam)
                  </div>
                </>
              )}
            </>
          )}

          {/* Notes — required for connected calls, optional otherwise */}
          {f.response && (
            <div>
              {f.response === 'connected' ? (
                <SectionLabel>Notes <span className="text-red-500">*</span></SectionLabel>
              ) : (
                <SectionLabel>Notes <span className="text-gray-400">(optional)</span></SectionLabel>
              )}
              <Textarea
                placeholder={f.response === 'connected' ? 'Add notes about the call (required)…' : 'Optional remarks about the call…'}
                value={f.notes}
                onChange={(e) => set({ notes: e.target.value })}
                rows={2}
                className="mt-1 text-xs"
                style={{ borderColor: f.response === 'connected' && !f.notes.trim() ? '#EF4444' : COLORS.border }}
                data-testid="call-notes-input"
              />
              {f.response === 'connected' && !f.notes.trim() && (
                <p className="text-[10px] text-red-500 mt-1" data-testid="call-notes-required-hint">
                  Notes are required for connected calls.
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="workflow-dialog-footer" data-testid="call-response-footer">
          <Button variant="outline" size="sm" className="h-8 text-xs" disabled={submitting} data-testid="call-response-cancel" onClick={() => onOpenChange(false)} style={{ borderColor: COLORS.border }}>
            Cancel
          </Button>
          <Button size="sm" className="h-8 text-xs" onClick={handleSubmit} disabled={submitting || disabled()}
            style={{ backgroundColor: f.response === 'not_connected' && f.nc_next_action === 'mark_spam' ? '#EF4444' : COLORS.accent }}
            data-testid="save-call-response-btn">
            {submitting ? 'Saving…' : f.next_action === 'book_counselling' ? 'Save & book counselling' : f.next_action === 'convert_to_admission' ? 'Convert & Save' : 'Save'}
          </Button>
        </DialogFooter>
      </WorkflowDialogContent>
    </Dialog>
    {booking && <CallBookingDialog booking={booking} onClose={() => setBooking(null)} onBooked={onSaved} />}
    </>
  );
}
